import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  styled,
  tooltipClasses,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useAnalytics } from 'use-analytics';
import { ReactComponent as PulseInfoIcon } from '../../../src/assets/pulse-info-icon.svg';
import { TrackEvent } from '../analytics';
import Carousel from '../reports/components/Carousel';

const SUGGESTIONS = [
  'For exploratory research, open-ended questions tend to be the most effective.',
  'For hypothesis testing, be clear and specific, especially regarding the output that you are seeking.',
  'Avoid using words like “you”; instead use “respondents”, “the audience”, or “users.”',
  'If the question is complex, it is best to break it down into two questions.',
];

const EXAMPLES = [
  'What is the most important thing to fix on this page, in order to improve the customer experience?',
  'What does the audience expect from this product based on this page?',
  'What would be the next steps that visitors take after visiting this page?',
  'What can be added to this page in order to encourage users to take action?',
];

const TipsTooltip = styled(({ className, maxWidth, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, maxWidth }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FCFCFC',
    color: theme.palette.text.primary,
    borderRadius: '24px',
    border: '1px solid #E0E0E0',
    maxWidth: maxWidth,
  },
}));

const Tips = () => {
  return (
    <Box p={2}>
      <Typography component="span" sx={{ fontSize: 12 }}>
        Tips on using the chat feature in WEVO Pulse:
      </Typography>
      <Box>
        <ul style={{ marginBottom: 0, paddingInlineStart: '24px' }}>
          {SUGGESTIONS.map((item, index) => (
            <li key={index}>
              <Typography fontSize="12px">{item}</Typography>
            </li>
          ))}
        </ul>
      </Box>
    </Box>
  );
};

const QuestionExample = ({ question, isSelected }) => (
  <Box
    sx={{
      minHeight: '70px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: isSelected ? 'rgba(43, 77, 101, 0.11)' : '#FCFCFC',
      borderRadius: '16px',
      border: '0.5px solid #E0E0E0',
      paddingX: 2,
      paddingY: 1,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(43, 77, 101, 0.11)',
      },
    }}>
    <Typography fontSize="11px">{question}</Typography>
  </Box>
);

const ChatInfo = ({ sessionId, currentInput, onQuestionSelection, showCarousel }) => {
  const [expandChatInfo, setExpandChatInfo] = useState(true);
  const [showToggleTooltip, setShowToggleTooltip] = useState(false);

  const { track } = useAnalytics();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMdAndUp = useMediaQuery(theme.breakpoints.up('md'));
  const isLgAndUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isXlAndUp = useMediaQuery(theme.breakpoints.up('xl'));

  const { width: containerWidth, ref: containerRef } = useResizeDetector();

  const handleChatInfoToggled = () => {
    setExpandChatInfo(!expandChatInfo);
    setShowToggleTooltip(false);
  };

  const handleQuestionClick = ({ item }) => {
    track(TrackEvent.SELECTED_SUGGESTED_PULSE_CHAT_QUESTION, { sessionId, question: item });
    onQuestionSelection(item);
  };

  const getNumOfItemsToDisplay = () => {
    if (isXlAndUp) {
      return 3.1;
    } else if (isLgAndUp) {
      return 2.5;
    } else if (isSm || isMdAndUp) {
      return 2.2;
    } else {
      return 1.2;
    }
  };

  return (
    <Grid container p={2} spacing={1}>
      <Grid item container justifyContent="space-between" alignItems="center" ref={containerRef}>
        <TipsTooltip placement="right" maxWidth={containerWidth * 0.9} title={<Tips />}>
          <IconButton aria-label="tips" size="small">
            <PulseInfoIcon />
          </IconButton>
        </TipsTooltip>
        <Box onClick={handleChatInfoToggled} sx={{ cursor: 'pointer' }}>
          {!expandChatInfo && (
            <Typography variant="caption" pr={2}>
              View Suggested Prompts
            </Typography>
          )}
          <Tooltip
            open={showToggleTooltip}
            title={expandChatInfo ? 'Hide suggested prompts' : ''}
            placement="top-end"
            disableFocusListener={true}
            onOpen={() => setShowToggleTooltip(true)}
            onClose={() => setShowToggleTooltip(false)}>
            <span>
              <IconButton sx={{ width: 14, height: 14 }}>
                {expandChatInfo ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Grid>
      <Grid
        item
        container
        spacing={1}
        alignItems="stretch"
        sx={{ display: expandChatInfo ? 'flex' : 'none', borderRadius: '20px' }}>
        {showCarousel ? (
          <Carousel
            items={EXAMPLES}
            itemComponent={({ index, style, item }) => {
              return <QuestionExample question={item} isSelected={item === currentInput} />;
            }}
            numItemsDisplayed={getNumOfItemsToDisplay()}
            itemHeight={isLgAndUp ? 80 : 90}
            backgroundColor={'#FFFFFF'}
            arrowHeight={16}
            onSelection={handleQuestionClick}
          />
        ) : (
          EXAMPLES.map((question, index) => (
            <Grid item xs={6} key={index} onClick={() => handleQuestionClick(question)}>
              <QuestionExample question={question} isSelected={question === currentInput} />
            </Grid>
          ))
        )}
      </Grid>
    </Grid>
  );
};

export default ChatInfo;
